import { useApolloClient } from '@apollo/client';
import dayjs from 'dayjs';
import { createContext, useContext, ReactNode, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import Loader from '../../components/common/Loader';
import assignNewLocation from '../../utils/assignNewLocation';
import extractFromJWT from '../../utils/extractFromJWT';
import { getToken, updateToken, onTokenUpdate } from '../../utils/session';
import { ExtendSessionDocument, ExtendSessionMutation, ExtendSessionMutationVariables } from './ExtendSession.api';
import { LogoutDocument, LogoutMutation, LogoutMutationVariables } from './Logout.api';
import { CurrentUserFragment, useFetchCurrentUserQuery } from './Usersession.api';

export type ContextValue = {
    token: string | null;
    user: CurrentUserFragment | null;
    sessionId: string | null;
    /** Clear local state only. Use when the server side session is already gone. */
    logout: () => void;
    /** Revoke the session on the server, then clear local state. Use for user initiated sign out. */
    signOut: () => Promise<void>;
    /**
     * Whether a user initiated sign out is in flight.
     *
     * Signing out revokes this very session, and the server announces that on the channel this
     * browser is listening to - so the sign out arrives back as a "you were revoked" event while
     * the request is still running. Anything reacting to that event has to ignore its own.
     */
    isSigningOut: () => boolean;
};

export const Context = createContext<ContextValue | null>(null);

export const useUserSession = () => {
    const context = useContext(Context);

    if (!context) {
        throw new Error('useUserSession must be used within a UserSessionProvider');
    }

    return context;
};

export function useUser(shouldThrowIfNull: true): CurrentUserFragment;
export function useUser(shouldThrowIfNull: false | undefined): CurrentUserFragment | null;
export function useUser(shouldThrowIfNull?: boolean): CurrentUserFragment | null;
export function useUser(shouldThrowIfNull = false): CurrentUserFragment | null {
    const { user } = useUserSession(); // Assuming useUserSession is correctly defined and imported

    if (shouldThrowIfNull && !user) {
        throw new Error('User is not available');
    }

    return user;
}

export type UserSessionProviderProps = { children: ReactNode };

const UserSessionProvider = ({ children }: UserSessionProviderProps) => {
    // get the initial token from the session
    // forcefully run validation when retrieving it
    const [currentToken, setCurrentToken] = useState(() => getToken(true));

    const sessionId = useMemo(() => {
        if (currentToken) {
            return (extractFromJWT(currentToken) as { sessionId: { $oid: string } }).sessionId.$oid as string;
        }

        return null;
    }, [currentToken]);

    // listen for token updates and update the state
    useEffect(() => onTokenUpdate(setCurrentToken), [setCurrentToken]);

    const apolloClient = useApolloClient();

    const navigate = useNavigate();

    // set for the whole of a user initiated sign out, and read by whatever listens for revocations
    const signingOutRef = useRef(false);
    const isSigningOut = useCallback(() => signingOutRef.current, []);

    // clear local state only. This is the right behaviour when the session is already gone on the
    // server - it has expired, or another device revoked it - and there is nothing left to revoke.
    const logout = useCallback(() => {
        // cleared here as well as in `signOut`. This provider wraps every route and is never
        // unmounted, so a flag left set outlives the sign out that set it - and the next session in
        // the same tab would have every genuine remote revocation ignored as self inflicted.
        signingOutRef.current = false;
        updateToken(null);
        navigate('/login', { replace: true });
    }, [navigate]);

    // user initiated sign out. Revoke the session on the server first, otherwise the bearer token
    // stays valid until it expires and a copy of it keeps working after "signing out".
    const signOut = useCallback(async () => {
        // revoking this session makes the server announce it on the channel this browser is
        // subscribed to. Left unguarded, that event lands mid-request and sends the tab to /login -
        // and under enforced SSO straight on to the provider, tearing down this call before it can
        // end the provider's own session. The user would then be signed silently back in.
        signingOutRef.current = true;

        let endSessionUrl: string | null = null;

        try {
            const response = await apolloClient.mutate<LogoutMutation, LogoutMutationVariables>({
                mutation: LogoutDocument,
            });

            endSessionUrl = response?.data?.endSessionUrl || null;
        } catch (error) {
            // the session may already be invalid; clearing locally is still the correct outcome
            console.error(error);
        }

        // drop the local token before leaving, so a failed round trip to the provider cannot leave
        // the browser holding a token for a session that is already revoked
        updateToken(null);

        if (endSessionUrl) {
            // a full page navigation, not a client side route change: the provider has to see this
            // request to end its own session, and it sends the browser back to /login afterwards
            assignNewLocation(endSessionUrl);

            return;
        }

        // no provider navigation, so this sign out ends here - and the flag has to end with it. Left
        // set (a password logout, a provider with no end session endpoint, a failed mutation) the next
        // login in this same tab would ignore every real revocation for as long as it stayed mounted.
        signingOutRef.current = false;

        navigate('/login', { replace: true });
    }, [apolloClient, navigate]);

    // query to fetch user document
    const { data, refetch, loading } = useFetchCurrentUserQuery({
        // use network only policy to avoid returning stale data
        fetchPolicy: 'cache-and-network',
    });

    // the following effect is responsible for refreshing the token
    useEffect(() => {
        if (!currentToken) {
            // there's nothing to be done if there is no token
            return () => undefined;
        }

        const { exp } = extractFromJWT(currentToken);
        const expirationDate = dayjs(exp * 1000);
        const delayBeforeExpiration = expirationDate.subtract(1, 'minute').diff(dayjs(), 'millisecond');

        // in browser the real type is `number`
        let timeoutId: NodeJS.Timeout | null = null;
        let mounted = true;

        const refreshToken = async () => {
            if (expirationDate.isBefore(dayjs())) {
                // we don't refresh if the token is expired
                timeoutId = null;
                logout();
            } else {
                timeoutId = null;
                // try to refresh the token
                try {
                    const response = await apolloClient.mutate<ExtendSessionMutation, ExtendSessionMutationVariables>({
                        mutation: ExtendSessionDocument,
                    });

                    updateToken(response?.data?.token || null);
                } catch (error) {
                    // print it out
                    console.info('Failed to renew the session');
                    console.error(error);
                    if (mounted) {
                        // if fail trigger a new refresh soon
                        timeoutId = setTimeout(refreshToken, 500);
                    }
                }
            }
        };

        timeoutId = setTimeout(refreshToken, delayBeforeExpiration);

        return () => {
            mounted = false;

            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [currentToken, logout, apolloClient]);

    // we want to refetch the user document whenever the token changed
    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentToken]);

    const context = useMemo(
        (): ContextValue => ({
            token: currentToken,
            // if there's no token then we right away nullify the user document
            user: currentToken ? data?.currentUser || null : null,
            sessionId,
            logout,
            signOut,
            isSigningOut,
        }),
        [currentToken, data, sessionId, logout, signOut, isSigningOut]
    );

    if (context.token && !context?.user && loading) {
        return <Loader />;
    }

    return <Context.Provider value={context}>{children}</Context.Provider>;
};

export default UserSessionProvider;
